<template>
  <div class="pay-page page-wrapper">
    <div class="page-header">
      <div class="container">
        <h1 class="page-title">报名缴费</h1>
        <p class="page-subtitle">Registration & Payment</p>
      </div>
    </div>

    <div class="page-content">
      <div class="container">
        <div class="pay-layout">
          <!-- 步骤指示器 -->
          <el-steps :active="currentStep" align-center class="pay-steps">
            <el-step title="选择类型" icon="el-icon-edit"></el-step>
            <el-step title="扫码支付" icon="el-icon-bank-card"></el-step>
            <el-step title="缴费成功" icon="el-icon-circle-check"></el-step>
          </el-steps>

          <!-- 步骤1：选择注册类型 -->
          <div v-show="currentStep === 0" class="step-content">
            <div class="form-card">
              <h3 class="card-title">您的信息</h3>
              <div class="user-info-display">
                <div class="info-row">
                  <span class="info-label">姓名：</span>
                  <span class="info-value">{{ userInfo.realname || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">邮箱：</span>
                  <span class="info-value">{{ userInfo.email || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">单位：</span>
                  <span class="info-value">{{ userInfo.organization || '-' }}</span>
                </div>
                <div class="info-row">
                  <span class="info-label">职务：</span>
                  <span class="info-value">{{ userInfo.title || '-' }}</span>
                </div>
              </div>
            </div>

            <div class="form-card">
              <h3 class="card-title">选择注册类型</h3>
              <div class="ticket-options">
                <div 
                  v-for="ticket in tickets" 
                  :key="ticket.type"
                  class="ticket-option"
                  :class="{ 'selected': selectedTicket === ticket.type }"
                  @click="selectedTicket = ticket.type"
                >
                  <div class="ticket-info">
                    <div class="ticket-name">{{ ticket.name }}</div>
                    <div class="ticket-desc">{{ ticket.desc }}</div>
                    <!-- 展示线上注册费和现场注册费 -->
                    <div class="ticket-price-details" v-if="ticket.earlyBirdPrice && ticket.earlyBirdPrice !== ticket.regularPrice">
                      <div class="ticket-price-item">
                        <span class="price-type">线上注册费：</span>
                        <span class="price-value">¥{{ ticket.earlyBirdPrice }}</span>
                      </div>
                      <div class="ticket-price-item">
                        <span class="price-type">现场注册费：</span>
                        <span class="price-value original-price">¥{{ ticket.regularPrice }}</span>
                      </div>
                    </div>
                    <!-- 如果只有一种价格 -->
                    <div class="ticket-price-details" v-else>
                      <div class="ticket-price-item">
                        <span class="price-value">¥{{ ticket.regularPrice || ticket.price }}</span>
                      </div>
                    </div>
                  </div>
                  <div class="ticket-price">
                    <span class="price-currency">¥</span>
                    <span class="price-value">{{ ticket.price }}</span>
                  </div>
                  <div class="ticket-check" v-if="selectedTicket === ticket.type">
                    <i class="el-icon-check"></i>
                  </div>
                </div>
              </div>
            </div>

            <div class="step-actions">
              <el-button size="large" @click="$router.go(-1)">返回</el-button>
              <el-button type="primary" size="large" @click="nextStep">下一步</el-button>
            </div>
          </div>

          <!-- 步骤2：扫码支付 -->
          <div v-show="currentStep === 1" class="step-content">
            <div class="payment-section">
              <h3 class="card-title">扫码支付</h3>
              <div class="payment-info">
                <div class="order-info">
                  <p class="amount">支付金额：<span>¥{{ currentTicket.price }}</span></p>
                </div>
                <div class="qrcode-wrapper">
                  <div class="qrcode-placeholder">
                    <img src="@/assets/images/skm.png" alt="微信支付二维码" class="qrcode-image" />
                  </div>
                  <p class="qrcode-tip">请使用微信扫描二维码完成支付</p>
                </div>
                <div class="payment-status">
                  <el-button type="success" plain icon="el-icon-check" @click="completePayment">
                    完成支付
                  </el-button>
                </div>
              </div>
            </div>
          </div>

          <!-- 步骤3：缴费成功 -->
          <div v-show="currentStep === 2" class="step-content">
            <div class="success-section">
              <div class="success-icon">
                <i class="el-icon-circle-check"></i>
              </div>
              <h2>缴费成功！</h2>
              <p class="success-msg">您已成功完成缴费，欢迎参加第一届明安图天文前沿论坛</p>
              <div class="success-info">
                <p><strong>会议时间：</strong>2026年8月6日 - 9日</p>
                <p><strong>会议地点：</strong>内蒙古 · 新城宾馆</p>
              </div>
              <div class="success-actions">
                <!-- <el-button type="primary" @click="$router.push('/user/registration')">查看报名记录</el-button> -->
                <el-button @click="$router.push('/')">返回首页</el-button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { registrationApi, conferenceApi } from '@/api'
import { getUserInfo } from '@/utils'

export default {
  name: 'Pay',
  data() {
    return {
      currentStep: 0,
      selectedTicket: 'student',
      submitting: false,
      orderNo: '',
      conferenceInfo: null,
      tickets: [],
      earlyBirdDeadline: ''
    }
  },
  computed: {
    userInfo() {
      return getUserInfo() || {}
    },
    currentTicket() {
      return this.tickets.find(t => t.type === this.selectedTicket) || {}
    }
  },
  async mounted() {
    // 检查是否登录
    const token = localStorage.getItem('token')
    if (!token) {
      this.$message.warning('请先登录')
      this.$router.push('/login?redirect=/pay')
      return
    }

    // 获取会议信息
    await this.fetchConferenceInfo()

    // 从URL参数获取票种
    const type = this.$route.query.type
    if (type && this.tickets.some(t => t.type === type)) {
      this.selectedTicket = type
    }
  },
  methods: {
    // 获取会议信息
    async fetchConferenceInfo() {
      try {
        const res = await conferenceApi.getDetail(1)
        this.conferenceInfo = res.data || null
        
        // 设置线上注册费截止日期
        if (this.conferenceInfo && this.conferenceInfo.early_bird_deadline) {
          this.earlyBirdDeadline = this.conferenceInfo.early_bird_deadline
        }
        
        // 根据当前时间判断是否还在线上注册费期间
        const now = new Date()
        const earlyBirdDate = this.earlyBirdDeadline ? new Date(this.earlyBirdDeadline) : null
        const isEarlyBird = earlyBirdDate && now <= earlyBirdDate
        
        // 设置票种信息
        this.tickets = [
          { 
            type: 'student', 
            name: '学生注册费', 
            price: isEarlyBird ? (this.conferenceInfo.early_bird_fee_student || 2000) : (this.conferenceInfo.fee_student || 2000), 
            desc: isEarlyBird ? `线上注册费优惠中（截止${this.earlyBirdDeadline}）` : '凭有效学生证购买，可享学生优惠价',
            earlyBirdPrice: this.conferenceInfo.early_bird_fee_student,
            regularPrice: this.conferenceInfo.fee_student,
            originalPrice: this.conferenceInfo.fee_student || 2000,
            isEarlyBird: isEarlyBird
          },
          { 
            type: 'teacher', 
            name: '注册费', 
            price: isEarlyBird ? (this.conferenceInfo.early_bird_fee || 2500) : (this.conferenceInfo.fee || 2500), 
            desc: isEarlyBird ? `线上注册费优惠中（截止${this.earlyBirdDeadline}）` : '全程参会资格+会议资料，可享学术交流机会',
            earlyBirdPrice: this.conferenceInfo.early_bird_fee,
            regularPrice: this.conferenceInfo.fee,
            originalPrice: this.conferenceInfo.fee || 2500,
            isEarlyBird: isEarlyBird
          }
        ]
      } catch (error) {
        console.error('获取会议信息失败:', error)
        // 如果获取失败，使用默认价格
        this.tickets = [
          { 
            type: 'student', 
            name: '学生注册费', 
            price: 2000, 
            desc: '凭有效学生证购买',
            earlyBirdPrice: null,
            regularPrice: 2000,
            originalPrice: 2000,
            isEarlyBird: false
          },
          { 
            type: 'teacher', 
            name: '注册费', 
            price: 2500, 
            desc: '全程参会资格+会议资料',
            earlyBirdPrice: null,
            regularPrice: 2500,
            originalPrice: 2500,
            isEarlyBird: false
          }
        ]
      }
    },
    nextStep() {
      if (!this.selectedTicket) {
        this.$message.warning('请选择注册类型')
        return
      }
      
      // 判断早鸟优惠时间是否已截止
      if (this.conferenceInfo && this.conferenceInfo.early_bird_deadline) {
        const now = new Date()
        const earlyBirdDate = new Date(this.conferenceInfo.early_bird_deadline)
        if (now > earlyBirdDate) {
          this.$message.warning('线上报名时间已截止')
          return
        }
      }
      
      // 先跳转到第二步，再弹出提示（只提示，不提交）
      this.currentStep = 1
      this.$nextTick(() => {
        this.$alert('<span style="color:red;font-size:16px;">请扫码后仔细核对付款方为呼和浩特市华辰会议服务有限公司，如收款公司不符请联系管理员</span><br/><br/><span style="color:red;font-size:16px;">付款请务必备注单位+姓名，以便核对</span>', '提示', {
          confirmButtonText: '我已知晓',
          dangerouslyUseHTMLString: true
        })
      })
    },
    async submitOrder() {
      if (this.submitting) return false
      this.submitting = true
      try {
        const data = {
          conference_id: 1,
          ticket_type: this.selectedTicket,
          amount: this.currentTicket.price
        }

        const res = await registrationApi.submit(data)
        this.orderNo = res.data.order_no || 'REG' + Date.now()
        return true
      } catch (error) {
        console.error('提交订单失败:', error)
        this.$message.error('提交订单失败，请稍后重试')
        return false
      } finally {
        this.submitting = false
      }
    },
    async completePayment() {
      const ok = await this.submitOrder()
      if (ok) {
        this.currentStep = 2
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.pay-page {
  background: #f8f9fc;
}

.pay-steps {
  background: #fff;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 30px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);
}

.step-content {
  animation: fadeIn 0.3s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.user-info-display {
  background: #f8f9fc;
  border-radius: 12px;
  padding: 20px;

  .info-row {
    display: flex;
    padding: 12px 0;
    border-bottom: 1px dashed #e0e0e0;

    &:last-child {
      border-bottom: none;
    }

    .info-label {
      width: 80px;
      color: #999;
      flex-shrink: 0;
    }

    .info-value {
      color: #333;
      font-weight: 500;
    }
  }
}

.form-card {
  background: #fff;
  border-radius: 16px;
  padding: 30px;
  margin-bottom: 24px;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 20px;
    color: #1a237e;
    margin-bottom: 24px;
    padding-bottom: 16px;
    border-bottom: 2px solid #f0f0f0;
  }
}

.ticket-options {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 20px;
}

.ticket-option {
  position: relative;
  border: 2px solid #e0e0e0;
  border-radius: 12px;
  padding: 24px;
  cursor: pointer;
  transition: all 0.3s;

  &:hover {
    border-color: #7c4dff;
  }

  &.selected {
    border-color: #1a237e;
    background: linear-gradient(135deg, rgba(26, 35, 126, 0.05) 0%, rgba(124, 77, 255, 0.05) 100%);
  }

  .ticket-name {
    font-size: 18px;
    font-weight: 600;
    color: #1a237e;
    margin-bottom: 8px;
  }

  .ticket-desc {
    font-size: 13px;
    color: #999;
    margin-bottom: 16px;
  }

  .ticket-price {
    .price-currency {
      font-size: 16px;
      color: #f56c6c;
    }

    .price-value {
      font-size: 32px;
      font-weight: 700;
      color: #f56c6c;
    }
  }

  .ticket-check {
    position: absolute;
    top: 12px;
    right: 12px;
    width: 24px;
    height: 24px;
    background: #1a237e;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    i {
      color: #fff;
      font-size: 14px;
    }
  }

  .ticket-price-details {
    margin-top: 8px;
    .ticket-price-item {
      display: flex;
      justify-content: space-between;
      align-items: center;
      font-size: 14px;
      margin-bottom: 4px;
      
      .price-type {
        color: #666;
      }
      
      .price-value {
        font-weight: 600;
        color: #f56c6c;
        
        &.original-price {
          color: #999;
          text-decoration: line-through;
        }
      }
    }
  }
}

.step-actions {
  text-align: center;
  margin-top: 30px;

  .el-button {
    min-width: 150px;
    height: 50px;
    font-size: 16px;
  }
}

.order-summary {
  background: #fff;
  border-radius: 16px;
  padding: 30px;
  max-width: 600px;
  margin: 0 auto;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 20px;
    color: #1a237e;
    margin-bottom: 24px;
    text-align: center;
  }

  .summary-item {
    display: flex;
    justify-content: space-between;
    padding: 14px 0;
    border-bottom: 1px dashed #eee;

    .label {
      color: #999;
    }

    .value {
      color: #333;
      font-weight: 500;
    }
  }

  .summary-divider {
    height: 2px;
    background: #f0f0f0;
    margin: 16px 0;
  }

  .summary-total {
    display: flex;
    justify-content: space-between;
    padding: 16px 0;

    .label {
      font-size: 18px;
      color: #333;
    }

    .value {
      font-size: 28px;
      font-weight: 700;
      color: #f56c6c;
    }
  }
}

.payment-section {
  background: #fff;
  border-radius: 16px;
  padding: 40px;
  max-width: 580px;
  margin: 0 auto;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  .card-title {
    font-size: 20px;
    color: #1a237e;
    margin-bottom: 24px;
  }

  .order-info {
    margin-bottom: 30px;

    p {
      color: #666;
      margin: 8px 0;
    }

    .amount span {
      font-size: 28px;
      font-weight: 700;
      color: #f56c6c;
    }
  }

  .qrcode-wrapper {
    margin-bottom: 30px;
  }

  .qrcode-placeholder {
    width: 300px;
    height: 526px;
    background: #f5f5f5;
    border-radius: 12px;
    margin: 0 auto 16px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    color: #999;

    .qrcode-image {
      width: 100%;
      height: 100%;
      object-fit: contain;
      border-radius: 12px;
    }
  }

  .qrcode-tip {
    color: #666;
    font-size: 14px;
  }
}

.success-section {
  background: #fff;
  border-radius: 16px;
  padding: 60px 40px;
  max-width: 600px;
  margin: 0 auto;
  text-align: center;
  box-shadow: 0 4px 15px rgba(0, 0, 0, 0.05);

  .success-icon {
    margin-bottom: 24px;

    i {
      font-size: 80px;
      color: #67c23a;
    }
  }

  h2 {
    font-size: 28px;
    color: #1a237e;
    margin-bottom: 16px;
  }

  .success-msg {
    color: #666;
    font-size: 16px;
    margin-bottom: 30px;
  }

  .success-info {
    background: #f8f9fc;
    border-radius: 12px;
    padding: 24px;
    margin-bottom: 30px;

    p {
      margin: 10px 0;
      color: #555;
    }
  }

  .success-actions {
    display: flex;
    justify-content: center;
    gap: 16px;
  }
}

@media screen and (max-width: 768px) {
  .ticket-options {
    grid-template-columns: 1fr;
  }

  .form-card .el-col {
    width: 100%;
  }
}
</style>
